// Protocol spoken between this shell and the React app it hosts in an iframe.
// Mirrored by react/src/shell/bridge-io.ts — keep both files in sync (same channel, same
// SHELL_VERSION).
//
// Both apps are served from the same origin, so no credentials are ever exchanged here: the
// iframe reads the exact same localStorage keys that AuthService writes. If the React app is
// ever moved to another origin, this protocol needs a one-time token exchange instead.
import { isSameOriginPath } from './embed-url'

export const SHELL_CHANNEL = 'nexedu-shell'
export const SHELL_VERSION = 1

interface BaseMessage {
  channel: string
  version: number
}

export interface ShellNavigateMessage extends BaseMessage {
  type: 'navigate'
  path: string
}

export interface ShellRouteChangedMessage extends BaseMessage {
  type: 'route-changed'
  path: string
}

export interface ShellSessionExpiredMessage extends BaseMessage {
  type: 'session-expired'
}

/**
 * An analytics event from the iframe. The shell owns the Amplitude client (SDK, API key,
 * identified user), so the hosted app can only ask it to report one; see
 * `ReactAppComponent.trackHostedEvent` for the whitelist check.
 */
export interface ShellTrackMessage extends BaseMessage {
  type: 'track'
  name: string
  properties: TrackedProperties
}

/** Flat scalar properties, the only shape Amplitude accepts. */
export interface TrackedProperties {
  [key: string]: string | number | boolean
}

/** Messages the iframe sends to this shell. */
export type ChildToParentMessage =
  | ShellNavigateMessage
  | ShellRouteChangedMessage
  | ShellSessionExpiredMessage
  | ShellTrackMessage

/** Messages this shell sends to the iframe. */
export type ParentToChildMessage = ShellNavigateMessage

/**
 * Validates and narrows a raw window message.
 * Returns null for anything that is not ours, was not sent by the expected frame, or comes
 * from another origin — callers can therefore trust the returned value.
 */
export function readShellMessage (
  event: MessageEvent,
  expectedSource: Window | null,
  expectedOrigin: string = window.location.origin
): ChildToParentMessage | null {
  if (!expectedSource || event.source !== expectedSource) {
    return null
  }
  if (event.origin !== expectedOrigin) {
    return null
  }

  const message = event.data
  if (!message || message.channel !== SHELL_CHANNEL || message.version !== SHELL_VERSION) {
    return null
  }

  if (message.type === 'route-changed' || message.type === 'navigate') {
    return isSameOriginPath(message.path) ? message as ChildToParentMessage : null
  }
  if (message.type === 'session-expired') {
    return message as ChildToParentMessage
  }
  if (message.type === 'track') {
    return readTrackMessage(message)
  }

  return null
}

export function buildNavigateMessage (path: string): ParentToChildMessage {
  return { channel: SHELL_CHANNEL, version: SHELL_VERSION, type: 'navigate', path }
}

const MAX_EVENT_NAME_LENGTH = 64
const MAX_SERIALIZED_PROPERTIES = 2000
// keys that would reach Object.prototype if copied blindly into a fresh object
const FORBIDDEN_KEYS = ['__proto__', 'constructor', 'prototype']

/**
 * Rebuilds the track message from scratch: only our envelope and flat scalar properties
 * survive. Anything else (nested objects, arrays, oversized payloads, prototype keys) is
 * dropped, and the caller gets null for the whole message.
 */
function readTrackMessage (message: Record<string, any>): ChildToParentMessage | null {
  const name = message.name
  if (typeof name !== 'string' || !name.length || name.length > MAX_EVENT_NAME_LENGTH) {
    return null
  }

  const properties = readProperties(message.properties)
  if (!properties) {
    return null
  }

  return {
    channel: SHELL_CHANNEL,
    version: SHELL_VERSION,
    type: 'track',
    name,
    properties
  } as ShellTrackMessage
}

function readProperties (raw: any): TrackedProperties | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null
  }

  const clean: TrackedProperties = {}
  const keys = Object.keys(raw)

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const value = raw[key]

    if (FORBIDDEN_KEYS.indexOf(key) !== -1) {
      return null
    }
    if (typeof value !== 'string' && typeof value !== 'boolean' &&
      !(typeof value === 'number' && isFinite(value))) {
      return null
    }

    clean[key] = value
  }

  return JSON.stringify(clean).length > MAX_SERIALIZED_PROPERTIES ? null : clean
}
